


































































































































































































































import {
  computed,
  defineComponent,
  reactive,
  Ref,
  ref,
  watch,
} from '@vue/composition-api';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import { useFormat, useRouter } from '@ideavivat/vivat-2';
import _ from 'lodash';

import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonModalWithDivideDelivery from '@/components/DeliveryNote/CommonModalWithDivideDelivery.vue';
import CommonModalWithDriverAbsenceEditor from '@/components/Driver/CommonModalWithDriverAbsenceEditor.vue';
import ViewDeliveryMarkerModal from '@/components/Delivery/ViewDeliveryMarkerModal.vue';
import CommonAreaCodeSelector from '@/components/Setting/CommonAreaCodeSelector.vue';
import ResourceSelectorInput from '@/components/DeliveryNote/ResourceSelectorInput.vue';
import CreateDeliveryNoteStats from '@/components/DeliveryNote/CreateDeliveryNoteStats.vue';
import WeightDisplay from '@/components/DeliveryNote/WeightDisplay.vue';
import AreaCode from '@/components/DeliveryNote/AreaCode.vue';
import LongDeliveryObject from '@/components/DeliveryNote/LongDeliveryObject.vue';
import Paginator from '@/components/Vivat/Paginator.vue';
import CommonDeliveryStatusSelect from '@/components/Delivery/CommonDeliveryStatusSelect.vue';
import CommonProvinceSelector from '@/components/LocationAddress/CommonProvinceSelector.vue';

import createDeliveryNote from '@/gql/deliveryNote/createDeliveryNote.gql';
import updateDeliveryNote from '@/gql/deliveryNote/updateDeliveryNote.gql';
import getDeliveryNote from '@/gql/deliveryNote/getDeliveryNote.gql';

import useCreateSingleDeliveryNote from '@/views/DeliveryNote/useCreateSingleDeliveryNote';
import useCreateMultiDeliveryNote, { getDeliveryNoteInformation } from '@/views/DeliveryNote/useCreateDeliveryNote';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';
import useDriverProfile from '@/useDriverProfile';
import useAuth from '@/useAuth';
import { BASE_DELIVERY_NOTE } from '@/useDeliveryNote';
import type { DeliveryNote } from '@/useDeliveryNote';

const NOW = useFormat.date().computerShortDate();

const CreateOrder = defineComponent({
  components: {
    CommonModalWithPreset,
    CommonModalWithDivideDelivery,
    CommonModalWithDriverAbsenceEditor,
    ViewDeliveryMarkerModal,
    CommonAreaCodeSelector,
    ResourceSelectorInput,
    WeightDisplay,
    CreateDeliveryNoteStats,
    AreaCode,
    LongDeliveryObject,
    Paginator,
    CommonDeliveryStatusSelect,
    CommonProvinceSelector,
  },
  setup() {
    const { route, router } = useRouter();
    const forceRender = ref(true);

    const deliveryDate = ref(NOW);
    const toCurrency = (number: string) => useFormat.number(number).currency();
    const displayDate = (date: string) => useFormat.date(date).displayFullThaiDate();
    const toDecimalFormat = (string: any, min = 0, max = 0) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: min, maximumFractionDigits: max });
    const { mapLastSevenDaysDeliveryNotes } = useDriverProfile();
    const { translateDistanceType } = useDeliveryAreaCode();
    const useCreateDeliveryNote = useCreateSingleDeliveryNote();
    const { me } = useAuth();
    const {
      sortDeliveryDisplay,
      sortDeliveryList,
    } = useCreateMultiDeliveryNote();
    const areaSort = ref(true);
    const hubs = computed(() => me.company.hubs || []);

    const deliveryNotes: Ref<DeliveryNote[]> = ref([]);

    const deliveryNote: Ref<DeliveryNote> = ref({ ...BASE_DELIVERY_NOTE });
    function startOver() {
      router.push({
        name: 'DeliveryNoteCreateSingle',
      });
      useCreateDeliveryNote.selectedDeliveries.value = [];
      useCreateDeliveryNote.selectedDeliveriesId.value = [];
      deliveryNote.value = { ...BASE_DELIVERY_NOTE };
    }

    const mapDNIntoFormat = (dn: any) => ({
      id: dn.id,
      ...dn,
      vehicle: {
        ...dn.vehicle,
        driver: dn.driver ? {
          id: dn.driver?.id,
          name: dn.driver.name,
          avatarUrl: dn.driver.avatarUrl,
          lastSevenDay: mapLastSevenDaysDeliveryNotes(dn.driver.paginatedDeliveryNotes.data),
        } : undefined,
      },
      deliveries: [
        ...dn.deliveries.map((delivery: any) => ({
          ...delivery,
          // packagesPrice: sumPackagesPrice(delivery.packages),
        })),
        ...dn.loggedDeliveries.map((delivery: any) => ({
          ...delivery,
          // packagesPrice: sumPackagesPrice(delivery.packages),
        })),
      ],
      tempDeliveriesId: dn.deliveries.map((delivery: any) => (delivery.id)),
    });

    // Assigning the deliveries in the delivery note into the selected array.
    function assignDeliveriesInDeliveryNoteIntoSelectedArray() {
      // eslint-disable-next-line
      (deliveryNoteResult.value.deliveries as any)?.forEach((item: any) => {
        useCreateDeliveryNote.selectedDeliveries.value.push(item);
        useCreateDeliveryNote.selectedDeliveriesId.value.push(item.id);
      });
    }

    // Watching the selectedDestinationsId in the filter object. If it changes, it will check if the
    // deliveryNote.value.deliveries.length is greater than 0 and if the destinationHub.id is equal to
    // the selectedDestinationsId. If all of these conditions are met, it will call the
    // assignDeliveriesInDeliveryNoteIntoSelectedArray() function.
    watch(() => useCreateDeliveryNote.filter.selectedDestinationsId, (newVal: any, oldVal: any) => {
      if (newVal !== oldVal && deliveryNote.value.deliveries.length > 0 && deliveryNote.value.deliveries[0].destinationHub.id === useCreateDeliveryNote.filter.selectedDestinationsId) {
        assignDeliveriesInDeliveryNoteIntoSelectedArray();
      }
    });

    const { result: getDeliveryNoteResult, loading: deliveryNoteLoading } = useQuery(getDeliveryNote, computed(() => ({
      id: route.value.params?.id || -1,
    })), () => ({ fetchPolicy: 'cache-and-network', enabled: route.value.name === 'DeliveryNoteEdit' }));
    const deliveryNoteResult = useResult(getDeliveryNoteResult, { ...BASE_DELIVERY_NOTE }, (data: any) => data.deliveryNote);
    function assignDeliveryNoteType() {
      if (deliveryNote.value.deliveries.length > 0) {
        useCreateDeliveryNote.filter.filterByType = deliveryNote.value.deliveries[0].type;
      }
    }
    watch(() => getDeliveryNoteResult.value, (newVal: any, oldVal: any) => {
      if (!deliveryNoteLoading.value && newVal?.id !== route.value.params.id) {
        deliveryNote.value = mapDNIntoFormat(deliveryNoteResult.value);
        deliveryDate.value = deliveryNote.value.deliveryDate;

        useCreateDeliveryNote.selectedDeliveriesId.value = [];
        useCreateDeliveryNote.selectedDeliveries.value = [];
        assignDeliveriesInDeliveryNoteIntoSelectedArray();
        assignDeliveryNoteType();
      }
    });

    const deliveryNoteInformation = computed(() => getDeliveryNoteInformation(useCreateDeliveryNote.selectedDeliveries.value));

    // Use to computed Arrange Panel size
    const showDestinationList = ref(true);
    const showAreaCodeList = ref(true);
    const showDeliveryNoteList = ref(true);
    const mainPanelWidth = computed(() => {
      let width = 8;
      if (showDestinationList.value) width += 18.8;
      if (showAreaCodeList.value) width += 18.8;
      if (showDeliveryNoteList.value) width += 18.8;
      if (showDestinationList && showAreaCodeList.value && showDeliveryNoteList.value) width -= 0.1;
      return `${width}rem`;
    });

    // Use to toggle Vehicle Selector modal
    const driverSelectorModal = ref(null);
    const vehicleSelectorModal = ref(null);
    function pendingSelectDriver() {
      (driverSelectorModal.value as any).open();
    }
    function setDriverOnVehicle(driver: any) {
      deliveryNote.value.driver = driver;
    }
    function pendingSelectVehicle() {
      (vehicleSelectorModal.value as any).open();
    }
    function setVehicleOnDeliveryNote(vehicle: any) {
      deliveryNote.value.vehicle = vehicle;
      if (!(deliveryNote.value.driver) && (deliveryNote.value.driver as any)?.id !== -1) {
        deliveryNote.value.driver = { ...vehicle.defaultDriver };
      }
    }

    const vehicleType = computed(() => deliveryNote.value.vehicle?.vehicleType || undefined);
    // Use to compare vehicle type with estimate delivery fee
    const computedVehicleTypeDeliveryCost = computed(() => {
      if (vehicleType.value) {
        switch (vehicleType.value as any) {
          case 'PICKUP':
            return deliveryNoteInformation.value?.pickupLeasedFee;
          case 'SIX_WHEELS':
            return deliveryNoteInformation.value?.sixWheelLeasedFee;
          case 'TEN_WHEELS':
            return deliveryNoteInformation.value?.tenWheelLeasedFee;
          case 'TRAILER':
            return deliveryNoteInformation.value?.trailerLeasedFee;
          default:
            return 0;
        }
      }
      return undefined;
    });

    // Handler DeliveryManCount
    function changeDeliveryManCount($event: number, index: number) { deliveryNotes.value[index].deliveryManCount = $event; }

    // Pending to save DeliveryNote
    const pendingToSaveIndex = ref(-1);
    const refetchQueries = ['getDeliveries', 'getVehicles', 'getDeliveryNotesForArrange', 'getDeliveryNote', 'getMostStableTotalItem', 'countDeliveryGroups'];
    const createDeliveryNoteInput = computed(() => ({
      // status: deliveryNote.status,
      status: route.value.params.id ? (deliveryNoteInformation.value?.status || deliveryNote.value.status) : 'PREPARE_TO_DELIVER',
      driver: { connect: parseInt((deliveryNote.value.driver as any)?.id, 10) },
      vehicle: { connect: parseInt((deliveryNote.value.vehicle as any)?.id, 10) },
      deliveries: {
        update: useCreateDeliveryNote.deliveries.value
          .filter((delivery: any) => useCreateDeliveryNote.selectedDeliveriesId.value.indexOf(delivery.id) > -1)
          .map((delivery: { id: string }, index: number) => ({
            id: parseInt(delivery.id, 10),
            order: index + 1,
            status: route.value.params.id ? undefined : 'PREPARE_TO_DELIVER',
          })),
        disconnect: deliveryNote.value.tempDeliveriesId?.filter((id: any) => useCreateDeliveryNote.selectedDeliveriesId.value.indexOf(id) === -1),
      },
      deliveryManCount: deliveryNote.value.deliveryManCount,
      distanceType: deliveryNoteInformation.value.distance || 'NA',
      deliveryDate: deliveryDate.value,
      area: deliveryNoteInformation.value?.area?.id ? { connect: deliveryNoteInformation.value.area?.id } : undefined,
      type: route.value.params.id ? undefined : 'NORMAL',
    }));
    const createDeliveryNoteMutation = useMutation(createDeliveryNote, () => ({
      variables: {
        input: createDeliveryNoteInput.value,
      },
      refetchQueries,
    }));
    const updateDeliveryNoteMutation = useMutation(updateDeliveryNote, () => ({
      variables: {
        id: route.value.params.id,
        input: {
          ...createDeliveryNoteInput.value,
          type: undefined,
          estimateDeliveryFee: deliveryNote.value.type === 'LEASED' ? computedVehicleTypeDeliveryCost.value : undefined,
        },
      },
      refetchQueries,
    }));
    const saving = ref(false);
    function pendingToSave() {
      saving.value = true;
      if (route.value.name === 'DeliveryNoteEdit') {
        updateDeliveryNoteMutation.mutate();
      } else createDeliveryNoteMutation.mutate();
    }

    const successCreateDeliveryNoteModal = ref(null);
    const errorCreateDeliveryNoteModal = ref(null);
    createDeliveryNoteMutation.onDone((result: any) => {
      deliveryNote.value.id = result.data.createDeliveryNote.id;
      deliveryNote.value.uniqueId = result.data.createDeliveryNote.uniqueId;
      (successCreateDeliveryNoteModal.value as any).compose({ action: 'สร้าง', entity: 'รายการ สำเร็จ' });
      saving.value = false;
    });
    updateDeliveryNoteMutation.onDone(() => {
      (successCreateDeliveryNoteModal.value as any).compose({ action: 'บันทึก', entity: 'รายการ สำเร็จ' });
      saving.value = false;
    });
    function mutationErrorHandler(mutationResponse: any) {
      let message = '';
      let entity = 'คนขับ';
      const responseMessage = mutationResponse.message;
      if (responseMessage === 'DRIVER' || responseMessage === 'LOADER') {
        switch (responseMessage) {
          case 'LOADER':
            entity = 'พนักงานยกของ';
            break;
          default:
            entity = 'คนขับ';
        }
        message = `โปรดตรวจเช็คค่าตอบแทน${entity} ให้ตรงกับรายการที่ท่านสร้าง`;
      }
      saving.value = false;
      (errorCreateDeliveryNoteModal.value as any).compose({ body: message });
    }
    createDeliveryNoteMutation.onError((res: any) => {
      mutationErrorHandler(res);
    });
    updateDeliveryNoteMutation.onError((res: any) => {
      mutationErrorHandler(res);
    });
    function saveHandler(deliveryNoteIndex: number, allowToSave: boolean) {
      if (deliveryNotes.value[deliveryNoteIndex].saved) {
        deliveryNotes.value[deliveryNoteIndex].saved = false;
      } else if (allowToSave) {
        pendingToSaveIndex.value = deliveryNoteIndex;
        if (deliveryNotes.value[deliveryNoteIndex].id > -1) updateDeliveryNoteMutation.mutate();
        else createDeliveryNoteMutation.mutate();
      } else {
        (errorCreateDeliveryNoteModal.value as any).compose({ body: 'โปรดเลือกคนขับรถ' });
      }
    }

    // Divide Delivery stuff
    const pendingDivideDelivery: Ref<number> = ref(-1);
    const divideDeliveryModal = ref(null);
    function openDivideDeliveryModal($event: number) {
      pendingDivideDelivery.value = $event;
      (divideDeliveryModal.value as any).open();
    }

    // Use to render map modal
    const viewMapModal = ref(null);
    const mapMarkers: Ref<{ delivery: any; lat: number; lng: number; open: boolean }[]> = ref([]);
    function pendingToViewMap() {
      const positionsLat: any[] = [];
      const positionsLong: any[] = [];
      mapMarkers.value = useCreateDeliveryNote.selectedDeliveries.value.filter((dv: any) => {
        const latIndex = positionsLat.indexOf(dv.computedLatLong.lat);
        const longIndex = positionsLong.indexOf(dv.computedLatLong.long);
        if ((latIndex !== -1 && (positionsLong.length > latIndex && positionsLong[latIndex] === dv.computedLatLong.long))
          || (longIndex !== -1 && (positionsLat.length > longIndex && positionsLat[longIndex] === dv.computedLatLong.lat))
        ) return undefined;
        positionsLat.push(dv.computedLatLong.lat);
        positionsLong.push(dv.computedLatLong.long);
        return dv;
      }).map((dv: any) => ({
        delivery: dv,
        open: false,
        lat: parseFloat(dv.computedLatLong.lat) >= -90 && parseFloat(dv.computedLatLong.lat) <= 90 ? parseFloat(dv.computedLatLong.lat) : 0,
        lng: parseFloat(dv.computedLatLong.long) >= -180 && parseFloat(dv.computedLatLong.long) <= 180 ? parseFloat(dv.computedLatLong.long) : 0,
      }));
      (viewMapModal.value as any).open();
    }

    const deliveryStats = computed(() => {
      const { total } = useCreateDeliveryNote.mostStableTotalItem.value.deliveries.paginatorInfo;
      const preNotArrange = total - useCreateDeliveryNote.selectedDeliveriesId.value.length;
      const notArrange = preNotArrange > 0 ? preNotArrange : 0;
      // if (route.value.name === 'DeliveryNoteEdit') {
      //   total = useCreateDeliveryNote.mostStableTotalItem.value.deliveries.paginatorInfo.total;
      //   notArrange = total - useCreateDeliveryNote.selectedDeliveriesId.value.length;
      // }
      return {
        total,
        arranged: useCreateDeliveryNote.selectedDeliveriesId.value.length,
        notArrange,
        resend: useCreateDeliveryNote.countResend.value,
      };
    });

    return {
      toCurrency,
      toDecimalFormat,
      displayDate,
      translateDistanceType,
      startOver,
      forceRender,
      deliveryDate,
      areaSort,

      me,
      hubs,
      deliveryStats,

      ...useCreateDeliveryNote,

      deliveryNote,
      deliveryNotes,
      saving,
      createDeliveryNote,
      createDeliveryNoteMutation,
      sortDeliveryDisplay,
      sortDeliveryList,

      createDeliveryNoteInput,

      successCreateDeliveryNoteModal,
      errorCreateDeliveryNoteModal,

      deliveryNoteInformation,
      computedVehicleTypeDeliveryCost,

      mainPanelWidth,
      showDestinationList,
      showAreaCodeList,
      showDeliveryNoteList,

      driverSelectorModal,
      pendingSelectDriver,
      setDriverOnVehicle,
      vehicleSelectorModal,
      pendingSelectVehicle,
      setVehicleOnDeliveryNote,

      changeDeliveryManCount,

      saveHandler,
      pendingToSave,

      divideDeliveryModal,
      pendingDivideDelivery,
      openDivideDeliveryModal,

      // use to view map on each DeliveryNote
      viewMapModal,
      mapMarkers,
      pendingToViewMap,
    };
  },
});

export default CreateOrder;
